<template>
  <div
    v-if="localPurchase"
    class="modal fade show d-block purchase-details-modal"
    tabindex="-1"
    aria-modal="true"
    role="dialog"
  >
    <div class="modal-backdrop fade show" @click="$emit('close')"></div>

    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable purchase-details-dialog">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header border-0 pb-0 align-items-start">
          <div>
            <span class="badge purchase-status-badge mb-2" :class="purchaseStatusClass(localPurchase.status)">
              {{ formatPurchaseStatus(localPurchase.status) }}
            </span>
            <h4 class="mb-1">{{ $t("purchase_page.purchase_label") }} {{ localPurchase.ref }}</h4>
            <p class="text-muted mb-0">
              {{ localPurchase.supplier_name || $t("purchase_page.not_available_short") }} •
              {{ localPurchase.warehouse_name || $t("purchase_page.not_available_short") }} •
              {{ formatDate(localPurchase.created_at || localPurchase.date) }}
            </p>
          </div>
          <button type="button" class="btn-close shadow-none" :aria-label="$t('common.close')" @click="$emit('close')"></button>
        </div>

        <div class="modal-body pt-4">
          <div class="row g-3 mb-4">
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("purchase_page.grand_total") }}</span>
                <h5>{{ formatMoney(localPurchase.grand_total) }}</h5>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("purchase_page.paid") }}</span>
                <h5 class="text-success">{{ formatMoney(localPurchase.paid) }}</h5>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("purchase_page.balance_due") }}</span>
                <h5 class="text-warning">{{ formatMoney(balanceDue) }}</h5>
              </div>
            </div>
            <div class="col-md-3 col-sm-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("purchase_page.payment_status") }}</span>
                <h5>
                  <span class="badge payment-status-badge" :class="paymentStatusClass(localPurchase.payment_status)">
                    {{ formatPaymentStatus(localPurchase.payment_status) }}
                  </span>
                </h5>
              </div>
            </div>
          </div>

          <div class="row g-4">
            <div class="col-lg-7">
              <div class="details-panel">
                <div class="panel-header">
                  <div>
                    <h5 class="mb-1">{{ $t("purchase_page.purchased_items") }}</h5>
                    <p class="text-muted mb-0">{{ $t("purchase_page.review_purchase_products") }}</p>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ $t("purchase_page.product") }}</th>
                        <th>{{ $t("purchase_page.warehouse") }}</th>
                        <th class="text-center">{{ $t("purchase_page.qty") }}</th>
                        <th class="text-end">{{ $t("purchase_page.unit_cost") }}</th>
                        <th class="text-end">{{ $t("purchase_page.subtotal") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in localPurchase.items || []" :key="item.id || `${item.product_id}-${index}`">
                        <td>{{ index + 1 }}</td>
                        <td>
                          <div class="fw-semibold">{{ item.product_name || $t("purchase_page.not_available_short") }}</div>
                          <div class="text-muted small">{{ item.product_code || $t("purchase_page.not_available_short") }}</div>
                        </td>
                        <td>{{ item.warehouse_name || localPurchase.warehouse_name || $t("purchase_page.not_available_short") }}</td>
                        <td class="text-center">{{ item.quantity }}</td>
                        <td class="text-end">{{ formatMoney(item.unit_cost) }}</td>
                        <td class="text-end fw-semibold">{{ formatMoney(item.subtotal) }}</td>
                      </tr>
                      <tr v-if="!(localPurchase.items || []).length">
                        <td colspan="6" class="text-center text-muted py-4">{{ $t("purchase_page.no_purchase_items_available") }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="col-lg-5">
              <div class="details-panel mb-4">
                <div class="panel-header">
                  <div>
                    <h5 class="mb-1">{{ $t("purchase_page.payment_activity") }}</h5>
                    <p class="text-muted mb-0">{{ $t("purchase_page.track_purchase_payments") }}</p>
                  </div>
                  <button
                    v-if="balanceDue > 0"
                    class="btn btn-primary btn-sm add-payment-btn"
                    @click="startCreatePayment"
                  >
                    <i class="ri-add-line me-1"></i>
                    {{ $t("purchase_page.add_payment") }}
                  </button>
                </div>

                <div class="payment-list">
                  <div
                    v-for="payment in sortedPayments"
                    :key="payment.id"
                    class="payment-item"
                  >
                    <div>
                      <div class="fw-semibold">{{ payment.method?.name || payment.paid_by || $t("purchase_page.payment") }}</div>
                      <div class="text-muted small">
                        {{ formatDate(payment.created_at) }}
                        <span v-if="payment.note">• {{ payment.note }}</span>
                      </div>
                    </div>
                    <div class="text-end">
                      <div class="fw-semibold">{{ formatMoney(payment.amount) }}</div>
                      <div class="payment-actions mt-2">
                        <button class="btn btn-sm btn-light" @click="startEditPayment(payment)">
                          {{ $t("purchase_page.edit") }}
                        </button>
                        <button class="btn btn-sm btn-outline-danger" @click="removePayment(payment)">
                          {{ $t("purchase_page.delete") }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-if="!sortedPayments.length" class="payment-empty-state">
                    <div class="payment-empty-icon">
                      <i class="ri-wallet-3-line"></i>
                    </div>
                    <h6 class="mb-2">{{ $t("purchase_page.no_payments_yet") }}</h6>
                    <p class="text-muted mb-0">
                      {{ $t("purchase_page.no_payment_activity_yet") }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="details-panel" v-if="showPaymentForm">
                <div class="panel-header">
                  <div>
                    <h5 class="mb-1">{{ editingPaymentId ? $t("purchase_page.edit_payment") : $t("purchase_page.add_payment") }}</h5>
                    <p class="text-muted mb-0">
                      {{ editingPaymentId ? $t("purchase_page.update_payment_entry") : $t("purchase_page.record_new_payment") }}
                    </p>
                  </div>
                  <button class="btn btn-sm btn-light" @click="resetPaymentForm">{{ $t("common.cancel") }}</button>
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">{{ $t("purchase_page.amount") }}</label>
                    <input
                      v-model.number="paymentForm.amount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">{{ $t("purchase_page.method") }}</label>
                    <select v-model.number="paymentForm.method_id" class="form-select payment-input">
                      <option :value="null">{{ $t("purchase_page.select_payment_method") }}</option>
                      <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                        {{ method.name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-12">
                    <label class="form-label">{{ $t("purchase_page.paid_by") }}</label>
                    <input v-model="paymentForm.paid_by" type="text" class="form-control payment-input" :placeholder="$t('purchase_page.paid_by_placeholder')" />
                  </div>
                  <div class="col-12">
                    <label class="form-label">{{ $t("purchase_page.note") }}</label>
                    <textarea v-model="paymentForm.note" rows="3" class="form-control payment-input" :placeholder="$t('purchase_page.optional_note')"></textarea>
                  </div>
                  <div class="col-12">
                    <button class="btn btn-primary w-100 payment-submit-btn" :disabled="isSubmittingPayment" @click="submitPayment">
                      <span v-if="isSubmittingPayment" class="spinner-border spinner-border-sm me-2"></span>
                      {{ editingPaymentId ? $t("purchase_page.update_payment") : $t("purchase_page.save_payment") }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="details-panel mt-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h5 class="mb-0">{{ $t("purchase_page.purchase_totals") }}</h5>
                </div>
                <div class="totals-row">
                  <span>{{ $t("purchase_page.subtotal") }}</span>
                  <strong>{{ formatMoney(subtotalAmount) }}</strong>
                </div>
                <div class="totals-row">
                  <span>{{ $t("purchase_page.tax") }}<span v-if="Number(localPurchase.tax_rate || 0) > 0"> ({{ Number(localPurchase.tax_rate).toFixed(2).replace(/\.00$/, "") }}%)</span></span>
                  <strong>{{ formatMoney(localPurchase.tax || 0) }}</strong>
                </div>
                <div class="totals-row">
                  <span>{{ $t("purchase_page.shipping") }}</span>
                  <strong>{{ formatMoney(localPurchase.shipping || 0) }}</strong>
                </div>
                <div class="totals-row" v-if="Number(localPurchase.discount || 0) > 0">
                  <span>{{ $t("purchase_page.discount") }}</span>
                  <strong>-{{ formatMoney(localPurchase.discount || 0) }}</strong>
                </div>
                <div class="totals-row grand">
                  <span>{{ $t("purchase_page.grand_total") }}</span>
                  <strong>{{ formatMoney(localPurchase.grand_total) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="print-only">
          <div class="invoice-container">
            <div class="invoice-header-top d-flex justify-content-between align-items-start mb-40">
              <div class="invoice-logo">
                <img src="../../../assets/img/logo.jpg" alt="Logo" class="mb-10" style="max-height: 50px;">
                <p class="fs-12 text-paragraph mb-0">ProCare Solution</p>
              </div>
              <div class="invoice-title-box text-end">
                <h1 class="invoice-title text-danger fw-bold mb-0">{{ $t("purchase_page.invoice") }}</h1>
                <p class="fs-14 fw-bold text-title mb-0">{{ $t("purchase_page.invoice_no") }}: {{ localPurchase.ref }}</p>
              </div>
            </div>

            <div class="invoice-info-section d-flex justify-content-between mb-40">
              <div class="bill-to">
                <p class="fs-12 fw-bold text-title mb-1 text-uppercase">{{ $t("purchase_page.bill_to") }}:</p>
                <h6 class="fs-16 fw-bold text-title mb-1 text-uppercase">{{ localPurchase.supplier_name }}</h6>
                <p class="fs-12 text-paragraph mb-0">{{ $t("purchase_page.supplier_code") }}: {{ localPurchase.supplier_code || $t("purchase_page.not_available_short") }}</p>
                <p class="fs-12 text-paragraph mb-0" v-if="localPurchase.warehouse_name">{{ $t("purchase_page.warehouse") }}: {{ localPurchase.warehouse_name }}</p>
              </div>
              <div class="invoice-summary-box text-end">
                <p class="fs-14 fw-bold text-title mb-1">{{ $t("purchase_page.total_due") }}:</p>
                <h4 class="fs-24 fw-bold text-title mb-2">{{ formatMoney(balanceDue) }}</h4>
                <p class="fs-14 text-title mb-0">{{ $t("purchase_page.date") }}: {{ formatDate(localPurchase.created_at || localPurchase.date) }}</p>
              </div>
            </div>

            <div class="invoice-items-table mb-20">
              <table class="table mb-0">
                <thead>
                  <tr class="bg-light">
                    <th class="text-center py-2 fs-14 fw-bold text-uppercase" style="width: 50px;">#</th>
                    <th class="py-2 fs-14 fw-bold text-uppercase">{{ $t("purchase_page.description") }}</th>
                    <th class="text-center py-2 fs-14 fw-bold text-uppercase">{{ $t("purchase_page.store") }}</th>
                    <th class="text-center py-2 fs-14 fw-bold text-uppercase">{{ $t("purchase_page.qty") }}</th>
                    <th class="text-end py-2 fs-14 fw-bold text-uppercase">{{ $t("purchase_page.cost") }}</th>
                    <th class="text-end py-2 fs-14 fw-bold text-uppercase">{{ $t("purchase_page.total") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in localPurchase.items || []" :key="item.id || `${item.product_id}-print-${index}`">
                    <td class="text-center py-3 fs-14 text-paragraph">{{ index + 1 }}</td>
                    <td class="py-3">
                      <div class="fs-14 fw-bold text-title">{{ item.product_name }}</div>
                    </td>
                    <td class="text-center py-3 fs-14 text-paragraph">{{ item.warehouse_name || localPurchase.warehouse_name }}</td>
                    <td class="text-center py-3 fs-14 text-paragraph">{{ item.quantity }}</td>
                    <td class="text-end py-3 fs-14 text-paragraph">{{ formatMoney(item.unit_cost) }}</td>
                    <td class="text-end py-3 fs-14 fw-bold text-title">{{ formatMoney(item.subtotal) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="row mb-40">
              <div class="col-7">
                <div class="payment-info mb-40">
                  <h6 class="fs-14 fw-bold text-title mb-3">{{ $t("purchase_page.payment_info") }}:</h6>
                  <p class="fs-12 text-paragraph mb-1">
                    <span class="fw-bold">{{ $t("purchase_page.status") }}:</span>
                    <span class="fw-bold text-capitalize">{{ formatPaymentStatus(localPurchase.payment_status) }}</span>
                  </p>
                  <p class="fs-12 text-paragraph mb-1" v-for="payment in sortedPayments" :key="payment.id || payment.created_at">
                    <span class="fw-bold">{{ $t("purchase_page.payment") }}:</span>
                    {{ payment.method?.name || payment.paid_by || $t("purchase_page.payment") }} - {{ formatMoney(payment.amount) }}
                  </p>
                </div>
              </div>
              <div class="col-5">
                <div class="invoice-totals text-end">
                  <div class="d-flex justify-content-between mb-2">
                    <span class="fs-14 fw-bold text-title text-uppercase">{{ $t("purchase_page.sub_total") }}</span>
                    <span class="fs-14 text-paragraph">{{ formatMoney(subtotalAmount) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2">
                    <span class="fs-14 fw-bold text-title text-uppercase">{{ $t("purchase_page.tax") }}<span v-if="Number(localPurchase.tax_rate || 0) > 0"> ({{ Number(localPurchase.tax_rate).toFixed(2).replace(/\.00$/, "") }}%)</span></span>
                    <span class="fs-14 text-paragraph">{{ formatMoney(localPurchase.tax || 0) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2">
                    <span class="fs-14 fw-bold text-title text-uppercase">{{ $t("purchase_page.shipping") }}</span>
                    <span class="fs-14 text-paragraph">{{ formatMoney(localPurchase.shipping || 0) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2" v-if="Number(localPurchase.discount || 0) > 0">
                    <span class="fs-14 fw-bold text-title text-uppercase">{{ $t("purchase_page.discount") }}</span>
                    <span class="fs-14 text-paragraph">-{{ formatMoney(localPurchase.discount || 0) }}</span>
                  </div>
                  <div class="d-flex justify-content-between bg-danger text-white p-2 mt-3">
                    <span class="fs-18 fw-bold text-uppercase">{{ $t("purchase_page.grand_total") }}</span>
                    <span class="fs-18 fw-bold">{{ formatMoney(localPurchase.grand_total) }}</span>
                  </div>
                  <div class="d-flex justify-content-between p-2 mt-1 border-bottom">
                    <span class="fs-14 fw-bold text-title text-uppercase">{{ $t("purchase_page.amount_paid") }}</span>
                    <span class="fs-14 text-paragraph">{{ formatMoney(localPurchase.paid) }}</span>
                  </div>
                  <div class="d-flex justify-content-between bg-dark text-white p-2 mt-1">
                    <span class="fs-16 fw-bold text-uppercase">{{ $t("purchase_page.balance_due") }}</span>
                    <span class="fs-16 fw-bold">{{ formatMoney(balanceDue) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button class="btn btn-light" @click="handlePrint">
            <i class="ri-printer-line me-1"></i>
            {{ $t("purchase_page.print") }}
          </button>
          <button class="btn btn-outline-danger" @click="handleDeletePurchase">
            <i class="ri-delete-bin-line me-1"></i>
            {{ $t("purchase_page.delete_purchase") }}
          </button>
          <button class="btn btn-dark" @click="$emit('close')">
            {{ $t("common.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch, type PropType } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import paymentMethodService, { type PaymentMethod } from "../../../services/paymentMethod";
import {
  addPurchasePayment,
  deletePurchase,
  deletePurchasePayment,
  updatePurchasePayment,
  type Purchase,
} from "../../../services/purchase";

type PaymentForm = {
  amount: number | null;
  method_id: number | null;
  paid_by: string;
  note: string;
};

export default defineComponent({
  name: "PurchaseDetails",
  props: {
    purchase: {
      type: Object as PropType<Purchase | null>,
      required: false,
      default: null,
    },
  },
  emits: ["close", "payment-added", "purchase-deleted"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const localPurchase = ref<Purchase | null>(null);
    const paymentMethods = ref<PaymentMethod[]>([]);
    const showPaymentForm = ref(false);
    const editingPaymentId = ref<number | null>(null);
    const isSubmittingPayment = ref(false);
    const paymentForm = ref<PaymentForm>({
      amount: null,
      method_id: null,
      paid_by: "",
      note: "",
    });

    const sortedPayments = computed(() =>
      [...(localPurchase.value?.payments || [])].sort((a, b) => {
        const first = new Date(a.created_at || "").getTime();
        const second = new Date(b.created_at || "").getTime();
        return second - first;
      })
    );

    const balanceDue = computed(() => {
      if (!localPurchase.value) return 0;
      return Math.max(Number(localPurchase.value.grand_total || 0) - Number(localPurchase.value.paid || 0), 0);
    });

    const subtotalAmount = computed(() => {
      if (!localPurchase.value) return 0;
      return Number(localPurchase.value.grand_total || 0)
        - Number(localPurchase.value.tax || 0)
        - Number(localPurchase.value.shipping || 0)
        + Number(localPurchase.value.discount || 0);
    });

    const formatDate = (dateString?: string) => {
      if (!dateString) return t("purchase_page.not_available_short");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatMoney = (value?: number | string) =>
      `$${Number(value || 0).toFixed(2)}`;

    const paymentStatusLabel = (status?: string) => {
      const normalized = String(status || "").trim().toLowerCase();
      return normalized === "due" ? "pending" : normalized || "pending";
    };

    const formatPaymentStatus = (status?: string) =>
      t(`purchase_page.payment_status_${paymentStatusLabel(status)}`);

    const formatPurchaseStatus = (status?: string) =>
      t(`purchase_page.purchase_status_${String(status || "pending").trim().toLowerCase() || "pending"}`);

    const paymentStatusClass = (status?: string) => {
      const normalized = String(status || "").trim().toLowerCase();
      return {
        "bg-success-subtle text-success": normalized === "paid",
        "bg-warning-subtle text-warning": normalized === "partial",
        "bg-danger-subtle text-danger": normalized === "pending" || normalized === "due",
      };
    };

    const purchaseStatusClass = (status?: string) => {
      const normalized = String(status || "").trim().toLowerCase();
      return {
        "bg-success-subtle text-success": normalized === "received",
        "bg-warning-subtle text-warning": normalized === "ordered" || normalized === "pending",
        "bg-info-subtle text-info": normalized === "sent",
      };
    };

    const resetPaymentForm = () => {
      showPaymentForm.value = false;
      editingPaymentId.value = null;
      paymentForm.value = {
        amount: balanceDue.value > 0 ? Number(balanceDue.value.toFixed(2)) : null,
        method_id: null,
        paid_by: "",
        note: "",
      };
    };

    const startCreatePayment = () => {
      resetPaymentForm();
      showPaymentForm.value = true;
    };

    const startEditPayment = (payment: NonNullable<Purchase["payments"]>[number]) => {
      editingPaymentId.value = payment.id ?? null;
      paymentForm.value = {
        amount: Number(payment.amount || 0),
        method_id: payment.method?.id || payment.method_id || null,
        paid_by: payment.paid_by || payment.method?.name || "",
        note: payment.note || "",
      };
      showPaymentForm.value = true;
    };

    const fetchPaymentMethods = async () => {
      try {
        paymentMethods.value = await paymentMethodService.getPaymentMethods();
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };

    const applyUpdatedPurchase = (updatedPurchase: Purchase | null | undefined) => {
      if (!updatedPurchase) return;
      localPurchase.value = updatedPurchase;
      emit("payment-added", updatedPurchase);
    };

    const submitPayment = async () => {
      if (!localPurchase.value?.id) return;

      const amount = Number(paymentForm.value.amount || 0);
      const methodId = paymentForm.value.method_id;

      if (!amount || amount <= 0) {
        await Swal.fire({
          icon: "warning",
          title: t("purchase_page.amount_required"),
          text: t("purchase_page.enter_valid_payment_amount"),
        });
        return;
      }

      if (!methodId) {
        await Swal.fire({
          icon: "warning",
          title: t("purchase_page.payment_method_required"),
          text: t("purchase_page.select_payment_method_text"),
        });
        return;
      }

      const selectedMethod = paymentMethods.value.find((item) => item.id === methodId);
      const payload = {
        amount,
        method_id: methodId,
        paid_by: paymentForm.value.paid_by || selectedMethod?.name || t("purchase_page.cash"),
        note: paymentForm.value.note || undefined,
      };

      isSubmittingPayment.value = true;

      try {
        const response = editingPaymentId.value
          ? await updatePurchasePayment(localPurchase.value.id, editingPaymentId.value, payload)
          : await addPurchasePayment(localPurchase.value.id, payload);

        applyUpdatedPurchase(response?.data ?? response);
        resetPaymentForm();

        await Swal.fire({
          icon: "success",
          title: editingPaymentId.value ? t("purchase_page.payment_updated") : t("purchase_page.payment_added"),
          text: response?.message || t("purchase_page.payment_saved_successfully"),
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error: any) {
        console.error("Error saving purchase payment:", error);
        await Swal.fire({
          icon: "error",
          title: t("purchase_page.payment_action_failed"),
          text: error?.response?.data?.message || t("purchase_page.unable_to_save_payment"),
        });
      } finally {
        isSubmittingPayment.value = false;
      }
    };

    const removePayment = async (payment: NonNullable<Purchase["payments"]>[number]) => {
      if (!localPurchase.value?.id || !payment.id) return;

      const result = await Swal.fire({
        title: t("purchase_page.delete_payment_title"),
        text: t("purchase_page.delete_payment_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: t("purchase_page.confirm_delete"),
        cancelButtonText: t("common.cancel"),
        reverseButtons: true,
      });

      if (!result.isConfirmed) return;

      try {
        const response = await deletePurchasePayment(localPurchase.value.id, payment.id);
        applyUpdatedPurchase(response?.data ?? response);

        await Swal.fire({
          icon: "success",
          title: t("purchase_page.payment_deleted"),
          text: response?.message || t("purchase_page.payment_removed_successfully"),
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error: any) {
        console.error("Error deleting purchase payment:", error);
        await Swal.fire({
          icon: "error",
          title: t("purchase_page.delete_failed"),
          text: error?.response?.data?.message || t("purchase_page.unable_to_delete_payment"),
        });
      }
    };

    const handleDeletePurchase = async () => {
      if (!localPurchase.value?.id) return;

      const result = await Swal.fire({
        title: t("purchase_page.delete_purchase_title"),
        text: t("purchase_page.delete_purchase_text", { ref: localPurchase.value.ref }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: t("purchase_page.confirm_delete"),
        cancelButtonText: t("common.cancel"),
        reverseButtons: true,
      });

      if (!result.isConfirmed) return;

      try {
        await deletePurchase(localPurchase.value.id);
        await Swal.fire({
          icon: "success",
          title: t("purchase_page.purchase_deleted"),
          text: t("purchase_page.purchase_removed_successfully"),
          timer: 1500,
          showConfirmButton: false,
        });
        emit("purchase-deleted");
      } catch (error: any) {
        console.error("Error deleting purchase:", error);
        await Swal.fire({
          icon: "error",
          title: t("purchase_page.delete_failed"),
          text: error?.response?.data?.message || t("purchase_page.unable_to_delete_purchase"),
        });
      }
    };

    const handlePrint = () => {
      window.print();
    };

    onMounted(fetchPaymentMethods);

    watch(
      () => props.purchase,
      (value) => {
        localPurchase.value = value ? JSON.parse(JSON.stringify(value)) : null;
        resetPaymentForm();
      },
      { immediate: true }
    );

    return {
      localPurchase,
      paymentMethods,
      showPaymentForm,
      editingPaymentId,
      isSubmittingPayment,
      paymentForm,
      sortedPayments,
      balanceDue,
      subtotalAmount,
      formatDate,
      formatMoney,
      paymentStatusLabel,
      formatPaymentStatus,
      formatPurchaseStatus,
      paymentStatusClass,
      purchaseStatusClass,
      startCreatePayment,
      startEditPayment,
      resetPaymentForm,
      submitPayment,
      removePayment,
      handleDeletePurchase,
      handlePrint,
      t,
    };
  },
});
</script>

<style scoped>
.purchase-details-modal {
  background: rgba(15, 23, 42, 0.35);
}

.print-only {
  display: none;
}

.purchase-details-dialog {
  max-width: 1240px;
  z-index: 1056;
}

.purchase-details-modal .modal-backdrop {
  z-index: 1055;
}

.purchase-details-dialog .modal-content {
  position: relative;
  z-index: 1056;
  border-radius: 20px;
}

.details-panel,
.summary-card {
  background: #fff;
  border: 1px solid #edf1f7;
  border-radius: 18px;
  padding: 20px;
}

.summary-card {
  height: 100%;
}

.summary-label {
  display: block;
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 8px;
}

.panel-header {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.payment-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-item {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 12px;
  border: 1px solid #edf1f7;
  border-radius: 14px;
  padding: 14px;
  background: #fafcff;
}

.payment-empty-state {
  border: 1px dashed #d6deeb;
  border-radius: 16px;
  padding: 28px 20px;
  text-align: center;
  background: linear-gradient(180deg, #fbfdff 0%, #f5f8fc 100%);
}

.payment-empty-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: #e9f2ff;
  color: #0d6efd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.payment-actions {
  display: flex;
  gap: 8px;
  justify-content: end;
}

.payment-input {
  min-height: 48px;
  border-radius: 12px;
  border-color: #dbe4f0;
  background: #fcfdff;
}

textarea.payment-input {
  min-height: 96px;
}

.payment-submit-btn {
  min-height: 48px;
  border-radius: 12px;
  font-weight: 600;
}

.add-payment-btn {
  padding: 6px 12px;
  font-size: 13px;
  line-height: 1.2;
  border-radius: 10px;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px dashed #e5e7eb;
}

.totals-row.grand {
  border-bottom: 0;
  padding-top: 16px;
  font-size: 18px;
}

.purchase-status-badge,
.payment-status-badge {
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.bg-success-subtle {
  background: rgba(25, 135, 84, 0.12);
}

.bg-warning-subtle {
  background: rgba(255, 193, 7, 0.18);
}

.bg-danger-subtle {
  background: rgba(220, 53, 69, 0.12);
}

.bg-info-subtle {
  background: rgba(13, 202, 240, 0.12);
}

@media print {
  @page {
    size: auto;
    margin: 8mm 10mm;
  }

  body * {
    visibility: hidden !important;
  }

  .purchase-details-modal,
  .purchase-details-modal * {
    visibility: visible !important;
  }

  .purchase-details-modal .btn-close,
  .modal-footer,
  .panel-header .btn,
  .payment-actions,
  .modal-backdrop,
  .modal-header,
  .modal-body {
    display: none !important;
  }

  .print-only {
    display: block !important;
    visibility: visible !important;
  }

  .purchase-details-modal,
  .purchase-details-dialog,
  .purchase-details-dialog .modal-content {
    position: static !important;
    background: #fff !important;
    box-shadow: none !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    overflow: visible !important;
  }

  .purchase-details-modal {
    position: absolute !important;
    inset: 0 !important;
    background: #fff !important;
  }

  .purchase-details-dialog {
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
  }

  .invoice-container {
    font-family: 'Inter', sans-serif;
    color: #333;
    background: white;
    width: 210mm;
    min-height: auto;
    padding: 20mm !important;
    margin: 0 auto !important;
    box-shadow: none !important;
    page-break-inside: avoid;
  }

  .invoice-title {
    font-size: 48px;
    letter-spacing: 2px;
  }

  .invoice-items-table .table thead th {
    border: none;
  }

  .invoice-items-table .table tbody td {
    border-color: #eee;
  }
}
</style>
